import sodium from 'libsodium-wrappers';

/** Wait until the libsodium runtime is ready before using any primitive. */
export async function initializeCrypto(): Promise<void> {
  await sodium.ready;
}

/**
 * Generate cryptographically secure random bytes.
 *
 * Security rationale: Uses libsodium's `randombytes_buf` which sources
 * entropy from the OS CSPRNG (/dev/urandom, CryptGenRandom on Windows).
 * This is the gold standard for key / nonce / salt generation.
 *
 * @param count - Number of random bytes to generate
 * @returns A Uint8Array of `count` cryptographically random bytes
 */
export function secureRandom(count: number): Uint8Array {
  return sodium.randombytes_buf(count);
}

/**
 * Constant-time comparison of two byte arrays.
 *
 * Security rationale: Prevents timing side-channel attacks where an
 * attacker measures response time to learn *which* byte position first
 * differs. libsodium's `memcmp` executes in constant time regardless
 * of where (or whether) the arrays diverge. Safe for comparing MACs,
 * signatures, and authentication hashes.
 *
 * @param a - First byte array
 * @param b - Second byte array
 * @returns `true` iff both arrays have identical content and length
 */
export function constantTimeCompare(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return sodium.memcmp(a, b);
}

/**
 * Securely zero out a byte array in memory.
 *
 * Security rationale: After sensitive data (keys, passwords, intermediate
 * secrets) is no longer needed it **must** be overwritten so that a memory
 * dump or cold-boot attack cannot recover it. libsodium's `memzero` uses
 * compiler-resistant `memset_s` / `SecureZeroMemory` so the write is
 * never optimised away.
 *
 * **Important**: The caller must not reference the array after calling
 * this function — it is now zeroed and must be considered invalid.
 *
 * @param buffer - The byte array to zero (modified in place)
 */
export function zeroMemory(buffer: Uint8Array): void {
  sodium.memzero(buffer);
}
